import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router }                               from '@angular/router';
import { FormGroup, FormBuilder, Validators }   from '@angular/forms';
import { Observable }                           from 'rxjs/Observable';

import { PuiAuthService,
         PuiAuthRequest,
         PuiAppSettings,
         PuiAppHooks,
         PuiDeactivateHook,
         PuiStore,
         PuiStorageService,
         PuiAuthStatus,
         PuiLoggingService}   from '../../../core/index';

const unauthorizedMsg = 'Invalid username/password';
const forbiddenMsg = 'You are not authorized';
const wiaResolution = 'Please contact your administrator for access';

@Component({
    selector: 'pui-login',
    templateUrl: 'login.component.html',
    styleUrls: ['login.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class PuiLoginComponent implements OnInit, OnDestroy, PuiDeactivateHook {
  rememberMeKey: string = 'username';
  message: string = undefined;
  description: string = undefined;
  appName = PuiAppSettings.appName;
  copyright = PuiAppSettings.copyright;
  loginForm: FormGroup;

  private storage: PuiStore;

  constructor(public authService: PuiAuthService,
              public router: Router,
              public hook: PuiAppHooks,
              public formBuilder: FormBuilder,
              private logger: PuiLoggingService,
              private storageService: PuiStorageService) {
    this.storage = storageService.getStore('login');
  }

  ngOnInit() {
    if (this.authService.isLoggedIn) {
      // already logged in
      this.router.navigate([this.authService.redirectUrl]);
    } else if (this.authService.useOAuth) {
      // redirect the user to the oauth site
      this.onOAuthLogin();
    } else if (this.authService.useWia) {
      // delegate to AuthService to handle the flow for WIA
      this.login({});
    } else {
      this.message = this.authService.message;
      this.loginForm = this.formBuilder.group({
        'username': [this.getRememberMeUsername() || '', Validators.required],
        'password': ['', Validators.required],
        'rememberMe': []
      });
    }
    document.querySelector('body').classList.add('login');
  }

  onOAuthLogin() {
    this.authService.login();
  }

  login(data: any) {
    this.message = undefined;

    const request: PuiAuthRequest = {
      password: data.password,
      username: data.username
    };

    this.authService.login(request).subscribe(response => {
      if (response.status === PuiAuthStatus.Success) {
        if (data.rememberMe) {
          this.rememberMe(data.username);
        } else if (data.username !== this.storage.getItem(this.rememberMeKey)) {
          this.clearRememberMe();
        }
        this.router.navigate([this.authService.redirectUrl]);
      } else {
        this.logger.error({
          message: response.message
        });
        this.displayMessage(response.message);
      }
    }, err => {
      this.logger.error({
        message: err.toString(),
        details: err
      });
      switch (err.status) {
        case PuiAuthStatus.Unauthorized:
          this.displayMessage(unauthorizedMsg);
          break;
        case PuiAuthStatus.Forbidden:
          this.displayMessage(forbiddenMsg);
          break;
        default:
        this.displayMessage(err);
      }
    });
  }

  private displayMessage(msg) {
    if (this.loginForm) {
      this.loginForm.reset();
    }
    this.message = msg || unauthorizedMsg;
    if (this.authService.useWia) {
      this.message += `<br><br>${wiaResolution}`;
    }
    this.clearRememberMe();
  }

  private clearRememberMe() {
    this.storage.removeItem(this.rememberMeKey);
  }

  private rememberMe(username: string) {
    if (!!username) {
      this.storage.setItem(this.rememberMeKey, username);
    }
  }

  private getRememberMeUsername() {
    return this.storage.getItem('username');
  }

  deactivateHook(): Observable<boolean> | Promise<boolean> | boolean {
    return this.hook.deactivateLogin();
  }

  public ngOnDestroy(): void {
    document.querySelector('body').classList.remove('login');
  }
}
